import { FACE_FINDER_AVAILABLE } from './faceFinderAvailability';
// client/src/pages/landingpages/LandingPageBuilder.js
import React, { useState, useRef, useEffect } from 'react';
import UploadSettings from "./UploadSettings";
import CaptureFlowSelect from './CaptureFlowSelect';
import { SERVER_URL } from '../../constants/server-type';
import MediaLibraryPanel from './MediaLibraryPanel';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import JoditEditorWrapper from './JoditEditorWrapper';
import { loadLandingPageFonts } from './landingPageFonts';
import { useParams, useNavigate } from 'react-router-dom';
import ColorSwatch from "../../utils/ColorSwatch";
import { apiFetch } from '../../api/api';
import { useOrg } from "../orgs/org-context";
import { useConfirmExit } from '../../hooks/useConfirmExit';
import { toast } from 'react-toastify';
import { getAllTemplates } from './templateService';
import { getOrgMediaPath } from '../../utils/media/getOrgMediaPath';
import {
  createLandingPage,
  updateLandingPage,
  getLandingPageById,
} from './lpService';
import './LandingPage.css';
import "../../styles/Buttons.css";

const BlockTypes = {
  IMAGE: 'image',
  TEXT: 'text',
  INPUT: 'input',
  BUTTON: 'button',
};

const screenSizes = {
  'iPhone 14': { width: 390, height: 844 },
  'Pixel 6': { width: 412, height: 915 },
  'Galaxy S22': { width: 360, height: 800 },
  'iPhone SE': { width: 375, height: 667 },
};


const LandingPageBuilder = () => {
  useEffect(() => { loadLandingPageFonts(); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [mobileColor, setMobileColor] = useState('#000');
  const [mobileBackColor, setMobileBackColor] = useState('#fff');
  const [mockupSize, setMockupSize] = useState(screenSizes['iPhone 14']);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [lpId, setLpId] = useState(id);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const { activeOrg, switchOrg } = useOrg();
  const prevOrgRef = useRef(null);
  const [curOrg, setCurOrg] = useState(activeOrg);
  const [templates, setTemplates] = useState([]);
  const [templateFields, setTemplateFields] = useState([]); // [{slot,label,type,visible}]
  const [savedSnapshot,setSavedSnapshot]=useState(null);
  const [saving,setSaving]=useState(false);
  const draft={name,description,color:mobileColor,backgroundColor:mobileBackColor,templateId:selectedTemplateId||'',blocks};
  const snapshot=JSON.stringify(draft);
  const latestSnapshot=useRef(snapshot);latestSnapshot.current=snapshot;
  const isDirty=!loading && savedSnapshot!==null && snapshot!==savedSnapshot;
  const baseline=page=>JSON.stringify({name:page.name||'',description:page.description||'',color:page.color||'#000',backgroundColor:page.backgroundColor||'#fff',templateId:page.templateId||'',blocks:Array.isArray(page.blocks)?page.blocks:[]});

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = Math.max(1, ...blocks.map(b => b.step || 1));
  const selectedBlock = blocks[selectedIndex];
  const isImageBlockActive = selectedBlock?.type === BlockTypes.IMAGE;

  const confirmExit = useConfirmExit({
    isDirty,
    onConfirm: () => {
      navigate('/lpages');
    },
    toastMessage: 'Unsaved landing page changes',
    confirmMessage: 'Leave landing page editor and discard changes?'
  });

  useEffect(() => {
    if (!prevOrgRef.current && activeOrg) {
      prevOrgRef.current = activeOrg;
    }
  }, [activeOrg]);


  useEffect(() => {
    if (!prevOrgRef.current || !activeOrg) return;
    if (activeOrg._id === prevOrgRef.current._id) return;

    confirmExit().then((allowed) => {
      if (allowed) {
        // accept new org
        prevOrgRef.current = activeOrg;
      } else {
        // rollback org
        switchOrg(prevOrgRef.current._id);
      }
    });
  }, [activeOrg]);


  const toastConfirm = (message, onConfirm) => {
    toast(
      ({ closeToast }) => (
        <div>
          <div style={{ marginBottom: 8 }}>{message}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                closeToast();
                onConfirm();
              }}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '6px 12px',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
            <button
              onClick={closeToast}
              style={{
                background: '#e5e7eb',
                border: 'none',
                padding: '6px 12px',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
      }
    );
  };




  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (id && id !== '0' && id !== 'add') {
          const page = await getLandingPageById(id);
          if (!page || page.message === 'Not Found') {
            toast.error('Landing page not found');
            navigate('/lpages');
            return;
          }
          setName(page.name || 'Untitled Landing Page');
          setDescription(page.description || '');
          setMobileColor(page.color || '#000');
          setMobileBackColor(page.backgroundColor || '#fff');
          setBlocks(Array.isArray(page.blocks) ? page.blocks : []);
          setSelectedTemplateId(page.templateId || '');
          setLpId(String(page._id));
          setSavedSnapshot(baseline(page));
        } else {
          setName('');
          setDescription('');
          setBlocks([]);
          setDescription('');setMobileColor('#000');setMobileBackColor('#fff');setSelectedTemplateId('');
          setLpId('add');
          setSavedSnapshot(baseline({}));

        }
      } catch (error) {
        console.error('Error loading landing page:', error);
        toast.error('Unable to load landing page');
        navigate('/lpages');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);



  useEffect(() => {
    if(!isDirty)return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);




  useEffect(() => {
    getAllTemplates().then(setTemplates).catch(console.error);
  }, []);


  useEffect(() => {
    let alive = true;

    (async () => {
      setTemplateFields([]);

      if (!selectedTemplateId) return;

      const tpl = await apiFetch(
        `/templates/getTemplateById/${selectedTemplateId}`,
        { method: 'GET' }
      );

      if (!alive) return;

      setTemplateFields(
        (tpl?.fields || [])
          .filter(f => f && (f.visible !== false))
          .map(f => ({
            fieldKey: f.key,      // ← FIX
            label: f.label,
            type: f.type
          }))
      );

    })();

    return () => { alive = false; };

  }, [selectedTemplateId]);


  const activeBlock = blocks[selectedIndex];
  const isImageBlock = activeBlock?.type === BlockTypes.IMAGE;

  const handleAddMediaToImageBlock = (media) => {
    if (!isImageBlockActive) return;

    handleBlockChange(selectedIndex, 'url', media.url);
    handleBlockChange(selectedIndex, 'mediaType', media.type);

    if (media.type === 'video') {
      handleBlockChange(selectedIndex, 'thumbnailUrl', media.thumbnailKey);
    } else {
      handleBlockChange(selectedIndex, 'thumbnailUrl', null);
    }
  };


  const handleReturn = async () => {
    console.log("isDirty:", isDirty)
    const ok = await confirmExit();
    if (ok) navigate('/lpages');
  };


  const handleTemplateSelect = (templateId) => {

    if (!activeOrg?._id) {
      toast.warn('Please select an organization before choosing a template');
      return;
    }

    setSelectedTemplateId(templateId);
    

  };


  const handleClear = () => {
    setBlocks([]);
    setSelectedIndex(null);
    setMobileColor('#000');
    setMobileBackColor('#fff');
    setMockupSize(screenSizes['iPhone 14']);
    setName('');
    setTemplates([]);
    setSelectedTemplateId('');
    setDescription('');
  };

  const handleSave = async () => {
    if(saving||!activeOrg?._id)return;
    const submitted=snapshot;
    const creating=!lpId || lpId==='add';
    const payload={...draft,orgId:activeOrg._id,name:name||'Untitled Landing Page'};
    setSaving(true);
    try {
      const result=lpId && lpId!=='add'?await updateLandingPage(lpId,payload):await createLandingPage(payload);
      if(!result?._id)throw new Error('Save response did not include the Landing Page identifier');
      setLpId(String(result._id));
      setSavedSnapshot(baseline(result));
      if(latestSnapshot.current===submitted){
        setName(result.name||'');setDescription(result.description||'');setMobileColor(result.color||'#000');setMobileBackColor(result.backgroundColor||'#fff');setSelectedTemplateId(result.templateId||'');setBlocks(result.blocks||[]);
        if(creating)navigate(`/lpages/edit/${result._id}`,{replace:true});
      }
      toast.success('Landing Page saved!');
    } catch(err) {toast.error(err.message||'Save failed');}
    finally{setSaving(false);}
  };

  const addNewStep = () => {
    const nextStep = Math.max(1, ...blocks.map(b => b.step ?? 1)) + 1;

    setCurrentStep(nextStep);
    handleAddBlock('text', nextStep);

    setBlocks(prev =>
      prev.map(b =>
        b.step === nextStep && b.type === 'text' && !b.config.text
          ? {
              ...b,
              config: {
                ...b.config,
                align: "center",
                text: `<br/><strong>Page ${nextStep}</strong><br/>Add content for this step.`,
              },
            }
          : b
      )
    );
  };


  const handleAddBlock = (type, stepOverride = null) => {
    const step = stepOverride ?? currentStep;

    let initialConfig = {};
    if (type === BlockTypes.TEXT) {
      initialConfig = {
        text: '',
        font: 'Arial',
        size: '16',
        color: '#000000',
        bold: false,
        italic: false,
        underline: false,
        align: 'left',
        backgroundColor: '#ffffff'
      };
    } else if (type === BlockTypes.INPUT) {
      initialConfig = {
        label: '',
        placeholder: '',
        font: 'Arial',
        size: '16',
        color: '#000000',
        bold: false,
        italic: false,
        underline: false,
        align: 'left',
        fieldColor: '#000000',
        fieldBackgroundColor: '#ffffff',
      };
    }  else if (type === BlockTypes.BUTTON) {
      initialConfig = {
        label: 'Submit',
        font: 'Arial',
        size: '16',
        color: '#ffffff',
        backgroundColor: '#007bff',
        borderColor: '#007bff',
        bold: false,
        italic: false,
        underline: false,
        hoverColor: '#ffffff',
        hoverBackgroundColor: '#0056b3',
        actionType: '',
        flow: 'captureOnly',
        captureMode: 'photo',
        faceFinder: false,
        linkUrl: '',
        position: 'left' // 👈 NEW
      };
    }

    const newBlock = { id: Date.now().toString(), type, step, config: initialConfig };
    const updated = [...blocks, newBlock];
    setBlocks(updated);
    setSelectedIndex(updated.length - 1);
  };

  const handleBlockChange = (index, field, value) => {
    const updated = [...blocks];
    updated[index].config[field] = value;
    setBlocks(updated);
    
  };

  const deleteBlock = (index) => {
    toastConfirm('Are you sure you want to delete this block?', () => {
      const updated = blocks.filter((_, i) => i !== index);
      setBlocks(updated);
      setSelectedIndex(null);
      
    });
  };

  const copyBlock = (index) => {
    const source = blocks[index];

    const copy = {
      ...blocks[index],
      config: { ...blocks[index].config },
      id: Date.now().toString(),
      step: source.step ?? currentStep, // stay on same page
    };
    const updated = [...blocks];
    updated.splice(index + 1, 0, copy);
    setBlocks(updated);
    setSelectedIndex(index + 1);
  };

  const moveBlockUp = (index) => {
    const currentBlock = blocks[index];
    const step = currentBlock.step ?? 1;

    // find previous block in SAME step
    const prevIndex = [...blocks]
      .slice(0, index)
      .map((b, i) => ({ b, i }))
      .reverse()
      .find(x => (x.b.step ?? 1) === step)?.i;

    if (prevIndex === undefined) return;

    const updated = [...blocks];
    [updated[prevIndex], updated[index]] =
      [updated[index], updated[prevIndex]];

    setBlocks(updated);
    setSelectedIndex(prevIndex);
  };

  const moveBlockDown = (index) => {
    const block = blocks[index];
    const step = block.step ?? 1;

    // 1️⃣ Try to move within SAME step
    const nextSameStepIndex = blocks
      .slice(index + 1)
      .map((b, i) => ({ b, i: i + index + 1 }))
      .find(x => (x.b.step ?? 1) === step)?.i;

    if (nextSameStepIndex !== undefined) {
      const updated = [...blocks];
      [updated[nextSameStepIndex], updated[index]] =
        [updated[index], updated[nextSameStepIndex]];
      setBlocks(updated);
      setSelectedIndex(nextSameStepIndex);
      return;
    }

    // 2️⃣ Otherwise, move block to NEXT page
    const updated = [...blocks];
    updated[index] = {
      ...block,
      step: step + 1,
    };
    setBlocks(updated);
    setSelectedIndex(index);
  };


  const NextPageSelector = ({ block, index, totalSteps }) => (
    <div className="input-pair">
      <label>Next Page</label>
      <select
        value={block.config.nextStep ?? ''}
        onChange={(e) =>
          handleBlockChange(
            index,
            'nextStep',
            e.target.value ? Number(e.target.value) : null
          )
        }
      >
        <option value="">Stay on current page</option>
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(p => (
          <option key={p} value={p}>Page {p}</option>
        ))}
      </select>
    </div>
  );


  const deletePage = (stepToDelete) => {
  toastConfirm(`Delete Page ${stepToDelete}? This will remove all blocks on that page.`, () => {
    const updated = blocks
      // remove blocks on this page
      .filter(b => (b.step || 1) !== stepToDelete)
      // shift later pages down
      .map(b => {
        const step = b.step || 1;
        return step > stepToDelete
          ? { ...b, step: step - 1 }
          : b;
      });

    setBlocks(updated);

    // keep currentStep valid
    if (currentStep > stepToDelete) {
      setCurrentStep(s => s - 1);
    } else if (currentStep === stepToDelete) {
      setCurrentStep(Math.max(1, stepToDelete - 1));
    }

    setSelectedIndex(null);
    
  });
};


  const computeScale = () => {
    const availableHeight = window.innerHeight - 60 - 40;
    return Math.min(1, availableHeight / (mockupSize.height+110));
  };

  const renderPreviewBlock = (block, index) => {
    const { type, config = {} } = block;

    const justify =
      config.align === 'center'
        ? 'center'
        : config.align === 'right'
        ? 'flex-end'
        : 'flex-start';

    const commonStyle = {
      color: config.color || '#000',
      fontFamily: config.font || config.fontFamily,
      fontSize: `${config.size || config.fontSize || 16}px`,
      whiteSpace: 'pre-wrap',
      width: '100%',
    };

    let content = null;

    if (type === BlockTypes.TEXT) {
      content = <div dangerouslySetInnerHTML={{ __html: config.text || '' }} />;
    }

    else if (type === BlockTypes.IMAGE) {
      content = (
        <div
          style={{
            display: 'flex',
            justifyContent: justify,
            width: '100%',
          }}
        >
          {config.mediaType !== 'video' && config.url && (
            <img
              src={config.url}
              alt=""
              style={{
                width: config.size || '100%',
                height: 'auto',
              }}
            />
          )}

          {config.mediaType === 'video' && config.url && (
            <video
              src={config.url}
              autoPlay
              muted
              loop
              playsInline
              style={{
                width: config.size || '100%',
                height: 'auto',
                borderRadius: '6px',
                background: '#000',
              }}
            />
          )}
        </div>
      );

  } else if (type === BlockTypes.INPUT) {
    const inputAlign =
      config.align === 'center'
        ? 'center'
        : config.align === 'right'
        ? 'flex-end'
        : 'flex-start';

    const labelStyle = {
      fontFamily: config.font || 'Arial',
      fontSize: `${config.size || 16}px`,
      fontWeight: config.bold ? 'bold' : 'normal',
      fontStyle: config.italic ? 'italic' : 'normal',
      textDecoration: config.underline ? 'underline' : 'none',
      color: config.color || '#000000',
      textAlign: config.align || 'left',
      width: '100%',
      whiteSpace: 'pre-wrap',
      marginBottom: '4px',
      boxSizing: 'border-box',
    };

    const inputStyle = {
      color: config.fieldColor || '#000000',
      backgroundColor: config.fieldBackgroundColor || 'transparent',
      fontFamily: config.font || 'Arial',
      fontSize: `${config.size || 16}px`,
      fontWeight: config.bold ? 'bold' : 'normal',
      fontStyle: config.italic ? 'italic' : 'normal',
      textDecoration: config.underline ? 'underline' : 'none',
      textAlign: config.align || 'left',
      width: `${config.width || 90}%`,
      whiteSpace: 'pre-wrap',
      marginBottom: '2px',
      paddingLeft: '6px',
      paddingRight: '6px',
      boxSizing: 'border-box',
    };

    content = (
      <div style={{ width: '100%', boxSizing: 'border-box' }}>
        <div style={labelStyle}>{config.label || ''}</div>

        <div
          style={{
            display: 'flex',
            justifyContent: inputAlign,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {config.inputType === 'textarea' ? (
            <textarea
              disabled
              rows={config.rows || 3}
              placeholder={config.placeholder || ''}
              style={{
                ...inputStyle,
                resize: 'none',
                pointerEvents: 'none',
              }}
            />
          ) : (
            <input
              type="text"
              disabled
              placeholder={config.placeholder || ''}
              style={{
                ...inputStyle,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>
    );
}


    const realIndex = blocks.findIndex(b => b.id === block.id);

    return (
      <div
        key={block.id}
        className={`blockPreview ${realIndex === selectedIndex ? 'selected' : ''}`}
        onClick={() =>
          setSelectedIndex(realIndex === selectedIndex ? null : realIndex)
        }
        style={{
          padding: '6px 0',          // ✅ remove horizontal drift
          width: '100%',
        }}
      >
        <div className="blockInner">
          <div style={commonStyle}>
            {content}
          </div>
        </div>
      </div>
    );
  };

  if (loading ) return <div>Loading...</div>;
  if (!activeOrg) {
    return <div>Loading organization…</div>;
  }
  const visibleBlocks = blocks.filter(
    b => (b.step ?? 1) === currentStep
  );

  const groupedBlocks = [];
  let currentRow = [];

  visibleBlocks.forEach((block) => {
    if (block.type === BlockTypes.BUTTON) {
      const pos = block.config.position || 'left';

      if (currentRow.length === 0 || pos === 'left') {
        if (currentRow.length > 0) groupedBlocks.push(currentRow);
        currentRow = [block];
      } else {
        currentRow.push(block);
      }
    } else {
      if (currentRow.length > 0) {
        groupedBlocks.push(currentRow);
        currentRow = [];
      }

      groupedBlocks.push(block);
    }
  });

  if (currentRow.length > 0) {
    groupedBlocks.push(currentRow);
  }
  console.log("blocks:", visibleBlocks)
  return (
    <div className="layoutWrapper no-x-scroll">
    {/* NEW: Media side panel */}
      <div className="mediaPanel">
        {/* placeholder for now */}
        <div className="mediaPanelHeader">Media</div>

        {/* this will be your MediaLibraryPanel later */}
        <div className="mediaPanelContent">
          <MediaLibraryPanel
            organizationId={activeOrg._id}
            type="image"
            canAdd={isImageBlockActive}
            onAdd={handleAddMediaToImageBlock}
          />
        </div>
      </div>
      <div className="editorPanel">
        <div className="editorHeader">
          <div className="row2 background-color-control">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div className="input-pair" style={{ marginLeft: '20px' }}>
                <label>Name:</label>
                <input type="text" value={name} onChange={(e) => {setName(e.target.value); }} placeholder="Landing Page Name" />
              </div>
              <div className="reg-buttons">
                <button onClick={handleSave} className="btn-green" disabled={saving}>{saving?'Saving?':'Save'}</button>
                <button onClick={handleReturn} className="btn-blue" style={{ marginLeft: '10px' }}>Return</button>
                <button onClick={handleClear} className="btn-red" style={{ marginLeft: '10px' }}>Clear All</button>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '9px', alignItems: 'center' }}>
              <div className="input-pair">
                <label>Background:</label>
                <Form.Group className="mb-2">
                  <ColorSwatch
                    value={mobileBackColor}
                    onChange={(color) => {
                      setMobileBackColor(color);
                      
                    }}
                  />
                </Form.Group>
            </div>
              <div className="input-pair">
                <label style={{ marginLeft: '16px' }}>Preview:</label>
                <select
                  value={Object.keys(screenSizes).find((k) => screenSizes[k] === mockupSize)}
                  onChange={(e) => {setMockupSize(screenSizes[e.target.value]); }}
                >
                  {Object.keys(screenSizes).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="input-pair">
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => {
                    handleTemplateSelect(e.target.value);
                    }
                  }
                >
                  <option value="">— Select Template —</option>
                  {templates.map(tpl => (
                    <option key={tpl._id} value={tpl._id}>
                      {tpl.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="header-buttons">
            <b>ADD:&nbsp;</b>
            <button className="btn-green" onClick={() => handleAddBlock(BlockTypes.IMAGE)}>Image Block</button>
            <button className="btn-green" onClick={() => handleAddBlock(BlockTypes.TEXT)}>Text Block</button>
            <button className="btn-green" onClick={() => handleAddBlock(BlockTypes.INPUT)}>Input Block</button>
            <button className="btn-green" onClick={() => handleAddBlock(BlockTypes.BUTTON)}>Button Block</button>
          </div>
          <hr />
          </div>
        <div className="editorBody">
          {blocks
          .filter(b => (b.step ?? 1) === currentStep)
          .map((block) => {
            const realIndex = blocks.findIndex(b => b.id === block.id);

            return (
              <div
                key={block.id}
                className={`sortableItem ${
                  realIndex === selectedIndex ? 'selected' : ''
                }`}
              >
                <div
                  className="blockHeader"
                  onClick={() =>
                    setSelectedIndex(
                      realIndex === selectedIndex ? null : realIndex
                    )
                  }
                >
                  <span>
                    {block.type.charAt(0).toUpperCase() + block.type.slice(1)} Block
                  </span>

                  <div className="header-buttons">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveBlockUp(realIndex);
                      }}
                      disabled={realIndex === 0}
                      className="btn-white"
                    >
                      ⬆
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveBlockDown(realIndex);
                      }}
                      disabled={realIndex === blocks.length - 1}
                      className="btn-white"

                    >
                      ⬇
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyBlock(realIndex);
                      }}
                      className="btn-white"
                    >
                      Copy
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteBlock(realIndex);
                      }}
                      className="btn-white"
                    >
                      ❌
                    </button>
                  </div>
                </div>

                {realIndex === selectedIndex && (
                  <div className="blockSettings">
                    {/* ---------- TEXT ---------- */}
                    {block.type === BlockTypes.TEXT && (
                      <JoditEditorWrapper
                        value={block.config.text || ''}
                        onBlur={(v) =>
                          handleBlockChange(realIndex, 'text', v)
                        }
                        onChange={(v) =>
                          handleBlockChange(realIndex, 'text', v)
                        }
                      />
                    )}

                    {/* ---------- IMAGE ---------- */}
                    {block.type === BlockTypes.IMAGE && (
                      <UploadSettings
                        handleBlockChange={handleBlockChange}
                        index={realIndex}
                        config={block.config}
                      />
                    )}

                    {/* ---------- INPUT ---------- */}
                    {block.type === BlockTypes.INPUT && (
                      <div className="input-settings-panel">
                        <div className="input-controls-wrap">

                          <div className="input-pair">
                            <label>Font</label>
                            <select
                              value={block.config.font || 'Arial'}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'font', e.target.value)
                              }
                            >
                              <option>Arial</option>
                              <option>Verdana</option>
                              <option>Georgia</option>
                              <option>Tahoma</option>
                            </select>
                          </div>

                          <div className="input-pair">
                            <label>Size</label>
                            <select
                              value={block.config.size || '16'}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'size', e.target.value)
                              }
                            >
                              {[12,14,16,18,20,24,28,32,36,40,44,48,52,56,60].map(s =>
                                <option key={s}>{s}</option>
                              )}
                            </select>
                          </div>

                          <div className="input-pair">
                            <label>Color</label>
                            <ColorSwatch
                              value={block.config.color || '#000000'}
                              onChange={(color) =>
                                handleBlockChange(realIndex, 'color', color)
                              }
                            />
                          </div>

                          <div className="input-pair">
                            <label>Align</label>
                            <select
                              value={block.config.align || 'left'}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'align', e.target.value)
                              }
                            >
                              <option>left</option>
                              <option>center</option>
                              <option>right</option>
                            </select>
                          </div>
                          <div className="input-pair">
                            <label>Input Type</label>
                            <select
                              value={block.config.inputType || 'text'}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'inputType', e.target.value)
                              }
                            >
                              <option value="text">Single Line</option>
                              <option value="textarea">Multi Line</option>
                            </select>
                          </div>
                          {block.config.inputType === 'textarea' && (
                            <div className="input-pair">
                              <label>Rows</label>
                              <input
                                type="number"
                                min="1"
                                value={block.config.rows || 3}
                                onChange={(e) =>
                                  handleBlockChange(realIndex, 'rows', parseInt(e.target.value))
                                }
                              />
                            </div>
                          )}
                          <div className="input-pair">
                            <label>Width (%)</label>
                            <input
                              type="number"
                              min="10"
                              max="100"
                              value={block.config.width || 90}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'width', parseInt(e.target.value))
                              }
                            />
                          </div>
                          <div className="input-pair">
                            <label>Prompt:</label>
                            <textarea
                              rows="2"
                              value={block.config.label || ''}
                              onChange={(e) => handleBlockChange(realIndex, 'label', e.target.value)}
                              placeholder="Enter label text..."
                              style={{ width: '100%', resize: 'vertical', whiteSpace: 'pre-wrap' }}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Placeholder:</label>
                            <input
                              type="text"
                              value={block.config.placeholder || ''}
                              onChange={(e) => handleBlockChange(realIndex, 'placeholder', e.target.value)}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Bind to Template Field:</label>
                            <select
                              value={block.config.fieldKey || ''}
                              disabled={templateFields.length === 0}
                              onChange={(e) => {
                                const fieldKey = e.target.value || '';
                                const f = templateFields.find(x => x.fieldKey === fieldKey);

                                const updated = [...blocks];

                                updated[realIndex].config.fieldKey = fieldKey;
                                updated[realIndex].config.fieldLabel = f?.label || '';

                                console.log("selected field:", fieldKey);
                                console.log("block config after set:", updated[realIndex].config);

                                setBlocks(updated);
                              }}
                            >
                              <option value="">— Select field —</option>

                              {templateFields.map(f => (
                                <option key={f.fieldKey} value={f.fieldKey}>
                                  {f.label}
                                </option>
                              ))}
                            </select>

                            {/*<select
                              value={block.config.mapSlot || ''}
                              disabled={templateFields.length === 0}
                              onChange={(e) => {
                                const slot = e.target.value || '';
                                const f = templateFields.find(x => x.slot === slot);
                                const label = f?.label || '';
                                const updated = [...blocks];
                                updated[realIndex].config.mapSlot = slot;
                                updated[realIndex].config.mapLabel = label;
                                // Optional: if no prompt label yet, use the template label as the prompt
                                if (!updated[realIndex].config.label && label) {
                                  updated[realIndex].config.label = label;
                                }
                                setBlocks(updated);
                              }}
                            >
                              <option value="">— Select field —</option>
                              {templateFields.map(f => (
                                <option key={f.slot} value={f.slot}>{f.label}</option>
                              ))}
                            </select> */}
                            {block.config.mapSlot && (
                              <div style={{ fontSize: 12, opacity: .8, paddingTop: 4 }}>
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                    )}

                    {/* ---------- BUTTON ---------- */}
                    {block.type === BlockTypes.BUTTON && (
                      <div className="input-settings-panel">
                        <div className="input-controls-wrap">
                          <div className="input-pair">
                            <label>Label:</label>
                            <input
                              size="10"
                              value={block.config.label || ''}
                              onChange={(e) => handleBlockChange(realIndex, 'label', e.target.value)}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Position</label>
                            <select
                              value={block.config.position || 'left'}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'position', e.target.value)
                              }
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                          <div className="input-pair">
                            <label>Size:</label>
                            <select
                              value={block.config.fontSize || '16'}
                              onChange={(e) => handleBlockChange(realIndex, 'fontSize', e.target.value)}
                            >
                              {[12,14,16,18,20,24,28,32,36,40,44,48,52,56,60].map((s) => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                          <div className="input-pair"><label>Bold:</label><input type="checkbox" checked={block.config.bold || false} onChange={(e) => handleBlockChange(realIndex, 'bold', e.target.checked)} /></div>
                          <div className="input-pair"><label>Italic:</label><input type="checkbox" checked={block.config.italic || false} onChange={(e) => handleBlockChange(realIndex, 'italic', e.target.checked)} /></div>
                          <div className="input-pair"><label>Underline:</label><input type="checkbox" checked={block.config.underline || false} onChange={(e) => handleBlockChange(realIndex, 'underline', e.target.checked)} /></div>
                          <div className="input-pair">
                            <label>Text:</label>
                            <ColorSwatch
                              value={block.config.color || '#000000'}
                              onChange={(color) => {
                                handleBlockChange(realIndex, 'color', color);
                                
                              }}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Background:</label>
                            <ColorSwatch
                              value={block.config.backgroundColor || '#ffffff'}
                              onChange={(backgroundColor) => {
                                handleBlockChange(realIndex, 'backgroundColor', backgroundColor);
                                
                              }}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Border:</label>
                            <ColorSwatch
                              value={block.config.borderColor || '#000000'}
                              onChange={(borderColor) => {
                                handleBlockChange(realIndex, 'borderColor', borderColor);
                                
                              }}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Hover Text:</label>
                            <ColorSwatch
                              value={block.config.hoverColor || '#ffffff'}
                              onChange={(hoverColor) => {
                                handleBlockChange(realIndex, 'hoverColor', hoverColor);
                                
                              }}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Hover Background:</label>
                            <ColorSwatch
                              value={block.config.hoverBackgroundColor || '#ffffff'}
                              onChange={(hoverBackgroundColor) => {
                                handleBlockChange(realIndex, 'hoverBackgroundColor', hoverBackgroundColor);
                                
                              }}
                            />
                          </div>
                          <div className="input-pair">
                            <label>Action</label>
                            <select
                              value={block.config.actionType || ''}
                              onChange={(e) =>
                                handleBlockChange(realIndex, 'actionType', e.target.value)
                              }
                            >
                              <option value=""> </option>
                              <option value="capture">Capture media</option>
                              <option value="submit">Submit</option>
                              <option value="goto">Go to page</option>
                              <option value="navigate">Navigate</option>
                            </select>
                          </div>

                          {(block.config.actionType === 'submit' ||
                            block.config.actionType === 'goto') && (
                            <NextPageSelector
                              block={block}
                              index={realIndex}
                              totalSteps={totalSteps}
                            />
                          )}
                          {block.config.actionType === 'capture' &&
                             block.config.captureMode === 'video' && (
                              <div className="input-pair">
                                <label>Max Video Seconds</label>
                                <select aria-label="Max Video Seconds" value={block.config.maxVideoSeconds!==undefined && (Number(block.config.maxVideoSeconds)<1 || Number(block.config.maxVideoSeconds)>30)?'':String(block.config.maxVideoSeconds||15)} onChange={e=>handleBlockChange(realIndex,'maxVideoSeconds',Number(e.target.value))}>
                                  {block.config.maxVideoSeconds!==undefined && (Number(block.config.maxVideoSeconds)<1 || Number(block.config.maxVideoSeconds)>30) && <option value="" disabled>Saved duration is outside 1?30 seconds. Choose a duration.</option>}
                                  {Number(block.config.maxVideoSeconds)>=1 && Number(block.config.maxVideoSeconds)<=30 && ![3,5,10,15,30].includes(Number(block.config.maxVideoSeconds)) && <option value={String(block.config.maxVideoSeconds)}>{block.config.maxVideoSeconds} seconds (existing)</option>}
                                  {[3,5,10,15,30].map(n=><option key={n} value={n}>{n} seconds</option>)}
                                </select>
                              </div>
                            )}

                          {block.config.actionType === 'capture' && (
                            <div className="input-pair">
                              <label>Capture Type</label>
                              <select
                                value={block.config.captureMode || 'photo'}
                                onChange={(e) =>
                                  handleBlockChange(realIndex, 'captureMode', e.target.value)
                                }
                              >
                                <option value="photo">Photo</option>
                                <option value="video">Video</option>
                              </select>
                            </div>
                          )}
                          {block.config.actionType === 'capture' && block.config.captureMode === 'photo' && (
                            <div className="input-pair">
                              <label>Capture Quality</label>
                              <select
                                value={block.config.captureQuality || 'standard'}
                                onChange={(e) =>
                                  handleBlockChange(realIndex, 'captureQuality', e.target.value)
                                }
                              >
                                <option value="standard">Standard (fast)</option>
                                <option value="hd">HD (print quality)</option>
                              </select>
                            </div>
                          )}
                          {block.config.actionType === 'capture' && (block.config.captureMode || 'photo') === 'photo' && (
                            <div className="input-pair">
                              <label htmlFor={`final-card-review-${block.id || realIndex}`}>Show Final Card Review</label>
                              <input
                                id={`final-card-review-${block.id || realIndex}`}
                                type="checkbox"
                                checked={block.config.showFinalCardReview === true}
                                onChange={(e) => handleBlockChange(realIndex, 'showFinalCardReview', e.target.checked)}
                              />
                            </div>
                          )}
                          {FACE_FINDER_AVAILABLE && block.config.actionType === 'capture' && (block.config.captureMode || 'photo') === 'photo' && (
                            <div className="input-pair">
                              <label>Face Finder</label>
                              <select aria-label="Face Finder" value={block.config.faceFinder === true ? 'on' : 'off'} onChange={e => handleBlockChange(realIndex, 'faceFinder', e.target.value === 'on')}>
                                <option value="off">Off</option><option value="on">On</option>
                              </select>
                            </div>
                          )}
                          {block.config.actionType === 'capture' && (
                            <CaptureFlowSelect
                              value={block.config.flow || 'captureOnly'}
                              onChange={flow => handleBlockChange(realIndex, 'flow', flow)}
                            />
                          )}

                          {block.config.actionType === 'navigate' && (
                            <div className="input-pair">
                              <label>Destination URL</label>
                              <input
                                value={block.config.linkUrl || ''}
                                onChange={(e) =>
                                  handleBlockChange(realIndex, 'linkUrl', e.target.value)
                                }
                              />
                            </div>
                          )}

                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mobile-preview-wrapper center-preview">
        <div className="page-controls">
          <button
            disabled={currentStep === 1}
            onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
          >
            ◀
          </button>
          <span>
            Page {currentStep} of {totalSteps}
          </span>
          <button
            onClick={() => setCurrentStep(s => Math.min(s + 1, totalSteps))}
            disabled={currentStep >= totalSteps}
          >
            ▶
          </button>
          <button
            onClick={addNewStep}
          >
            + Add Page
          </button>
          {totalSteps > 1 && (
            <button
              onClick={() => deletePage(currentStep)}
              className="danger"
            >
              Delete Page
            </button>
          )}
        </div>
        <div className="mobile-mockup-scale-wrapper">
          <div
            className="mobile-mockup-container"
            style={{
              '--mockup-scale': computeScale(),
              '--mockup-width': `${mockupSize.width}px`,
              '--mockup-height': `${mockupSize.height}px`,
            }}
          >
            <div className="previewHeader">
              Preview — Page {currentStep}
            </div>
            <div className="mobile-screen" style={{ backgroundColor: mobileBackColor }}>
            <div className="mobile-viewport">
              {groupedBlocks.map((group, i) => {
                // Single block
                if (!Array.isArray(group)) {
                  return renderPreviewBlock(group, i);
                }

                // BUTTON ROW
                return (
                  <div
                    key={`row-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent:
                        group.some(b => b.config.position === 'center')
                          ? 'center'
                          : group.some(b => b.config.position === 'right')
                          ? 'flex-end'
                          : 'flex-start',
                      gap: '8px',
                      marginBottom: '6px',
                    }}
                  >
                    {group.map((btnBlock) => {
                      const cfg = btnBlock.config || {};
                      const fontSize = parseInt(cfg.fontSize || 16);

                      return (
                        <button
                          key={btnBlock.id}
                          style={{
                            fontSize: `${fontSize}px`,
                            minWidth: `${fontSize * 3}px`,
                            padding: `${fontSize * 0.4}px ${fontSize * 0.8}px`, // 👈 scales button
                            borderRadius: `${fontSize * 0.3}px`,
                            backgroundColor: cfg.backgroundColor || '#007bff',
                            color: cfg.color || '#fff',
                            border: `2px solid ${cfg.borderColor || '#007bff'}`,
                            fontWeight: cfg.bold ? 'bold' : 'normal',
                            fontStyle: cfg.italic ? 'italic' : 'normal',
                            textDecoration: cfg.underline ? 'underline' : 'none',
                          }}
                        >
                          {cfg.label || 'Button'}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageBuilder;
